import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
    CreateCollection,
    DeleteCollection,
    ForceCollectionRefresh,
    SetCollections,
    UpdateCollection,
} from 'src/app/components/common/collection/collection.actions';
import { AppState } from 'src/app/models/app-state';
import { Collection } from 'src/app/models/collection';
import {
    selectCollections,
    selectShouldForceCollectionRefresh,
} from '../components/common/collection/collection.reducer';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { CollectionResourceService } from './collection-resource.service';

@Injectable({ providedIn: 'root' })
export class CollectionService {
    private isAuthenticated: boolean;

    constructor(
        private resource: CollectionResourceService,
        private store: Store<AppState>,
        userAuthService: UserAuthService
    ) {
        userAuthService.isAuthenticated$.subscribe(
            (isAuthenticated) => (this.isAuthenticated = isAuthenticated)
        );
    }

    // #region Get
    /**
     * @description Get collections for logged in user (returns cached collections for future requests).
     */
    getCollections$(): Observable<Collection[]> {
        if (!this.isAuthenticated) {
            return of([] as Collection[]);
        }

        return this.store.select(selectShouldForceCollectionRefresh).pipe(
            switchMap((shouldForce) => {
                if (shouldForce) {
                    return this.resource.getCollections(true).pipe(
                        switchMap((collections) => {
                            this.store.dispatch(
                                new SetCollections(collections)
                            );
                            this.store.dispatch(
                                new ForceCollectionRefresh(false)
                            );
                            return this.store.pipe(select(selectCollections));
                        })
                    );
                } else {
                    return this.store.pipe(select(selectCollections));
                }
            })
        );
    }

    getCollectionById(
        id: string,
        forceRefresh = false
    ): Observable<Collection> {
        if (!forceRefresh) {
            return this.getCollections$().pipe(
                take(1),
                switchMap((collections) => {
                    const foundCollection = collections.find(
                        (collection) => collection.id === id
                    );

                    if (!foundCollection) {
                        return this.getCollectionFromResource(id);
                    }

                    return of(foundCollection.clone());
                })
            );
        }

        return this.getCollectionFromResource(id);
    }

    getCollectionWithContents(collectionId: string): Observable<Collection> {
        return this.getCollectionById(collectionId).pipe(
            switchMap((collection) => {
                return forkJoin([
                    of(collection),
                    this.resource.getCollectionContents(collectionId),
                ]);
            }),
            map(([collection, contentCollection]) => {
                collection.setContentCollection(contentCollection);
                return collection;
            })
        );
    }
    // #endregion

    // #region Create
    createCollection(collection: Collection): Observable<Collection> {
        return this.resource.createCollection(collection).pipe(
            tap((newCollection) => {
                this.store.dispatch(new CreateCollection(newCollection));
            }),
            switchMap((newCollection) =>
                this.getCollectionById(newCollection.id, true)
            )
        );
    }
    // #endregion

    // #region Update
    updateCollection(collection: Collection): Observable<Collection> {
        collection.modificationDate = undefined;

        return this.resource.updateCollection(collection).pipe(
            // update store with local changes
            tap(() => {
                this.store.dispatch(new UpdateCollection(collection));
            }),
            // update store with changes from services (snapshots, etc.)
            switchMap(() => this.getCollectionById(collection.id, true))
        );
    }
    // #endregion

    // #region Delete
    deleteCollection(collectionId: string): Observable<void> {
        return this.resource.deleteCollection(collectionId).pipe(
            tap(() => {
                this.store.dispatch(new DeleteCollection(collectionId));
            })
        );
    }

    deleteSelectedCollections(
        collections: Collection[]
    ): Observable<{ id: string; success: boolean }[]> {
        if (!collections || collections.length === 0) {
            return of([] as { id: string; success: boolean }[]);
        }

        const resultObservables = collections.map((collection: Collection) => {
            return this.deleteCollection(collection.id).pipe(
                catchError(() => {
                    return of('failed');
                }),
                map((result) => {
                    return { id: collection.id, success: result !== 'failed' };
                })
            );
        });

        return forkJoin(resultObservables);
    }
    // #endregion

    private getCollectionFromResource(id: string): Observable<Collection> {
        return this.resource.getCollectionById(id).pipe(
            tap((collection) => {
                this.store.dispatch(new UpdateCollection(collection));
            })
        );
    }
}
